import React, { useState, useEffect } from "react";
import { FiX, FiTrash2, FiCalendar, FiClock, FiUser, FiMapPin, FiAlertTriangle, FiFileText, FiAlertCircle, FiCheck, FiMail } from "react-icons/fi";
import { getStaff } from "../../services/staffService";
import { getRooms } from "../../services/roomService";
import clientService from "../../services/clientService";
import appointmentService from "../../services/appointmentService";
import { logActivity, ACTIVITY_TYPES } from "../../services/activityService";
import { convertToAppointment } from "../../services/waitlistService";
import { tenantDataService } from "../../services/tenantDataService";
import settingsService from "../../services/settingsService";
import { useAuth } from "../../context/AuthContext";
import { useTenant } from "../../context/TenantContext";
import { getFunctions, httpsCallable } from 'firebase/functions';
import InvoiceModal from "../billing/InvoiceModal";
import toast from "react-hot-toast";
import { buildRRule, expandRRule, getRRuleDescription, getDefaultMonthlyMode } from "../../lib/recurrence/rrule";
import { checkConflicts, getConflictSummary, formatConflictMessage } from "../../lib/conflicts/checkConflicts";
import { checkAppointmentConflicts, checkRecurringConflicts } from "../../services/conflictDetectionService";
import { localToUtc, utcToLocal } from "../../utils/utcTimezone";
import { getUserTimezone, timezonesAreDifferent, formatTimeWithTimezone } from "../../utils/timezone";
import { getDetailedConflictInfo } from "../../utils/conflictSolutions";
import ConflictTooltip from "../common/ConflictTooltip";
import RecurringConflictDialog from "./RecurringConflictDialog";

// timeOptions will be generated dynamically from business hours

// Treatment types will be loaded dynamically from clinic settings

// Day names for weekly chips
const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAY_FULL_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function AppointmentModal({ 
  slot, 
  existing, 
  onClose, 
  onSaved, 
  onDelete, 
  clients = [], 
  appointments = [], 
  hours = [],
  waitlistData = null,
  onWaitlistRefresh = null
}) {
  const { currentTenant } = useTenant();
  const { userProfile } = useAuth();

  // Keyboard shortcuts for modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  
  // Map existing to appointment for backward compatibility
  const appointment = existing;
  // Tab state
  const [activeTab, setActiveTab] = useState('appointment');
  
  // Form state
  const [date, setDate] = useState("");
  
  // Generate time options from business hours for the selected date
  const [timeOptions, setTimeOptions] = useState([]);
  
  // Load business hours and generate time options
  useEffect(() => {
    const loadBusinessHours = async () => {
      if (!currentTenant?.id) return;
      
      try {
        tenantDataService.setTenantContext(currentTenant.id);
        const settingsDoc = await tenantDataService.getById('settings', 'clinic');
        
        if (settingsDoc && settingsDoc.businessHours && date) {
          const selectedDate = new Date(date);
          const weekday = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][selectedDate.getDay()];
          const dayHours = settingsDoc.businessHours[weekday];
          
          if (dayHours && !dayHours.closed) {
            // Generate 30-minute intervals between open and close times
            const generateTimeSlots = (startTime, endTime) => {
              const [startHour, startMin] = startTime.split(':').map(Number);
              const [endHour, endMin] = endTime.split(':').map(Number);
              
              const startSlot = startHour * 2 + (startMin >= 30 ? 1 : 0);
              const endSlot = endHour * 2 + (endMin > 30 ? 1 : 0);
              const slotCount = endSlot - startSlot;
              
              return Array.from({ length: slotCount }, (_, i) => {
                const totalSlot = startSlot + i;
                const hh = String(Math.floor(totalSlot / 2)).padStart(2, "0");
                const mm = totalSlot % 2 === 0 ? "00" : "30";
                return `${hh}:${mm}`;
              });
            };
            
            const slots = generateTimeSlots(dayHours.open, dayHours.close);
            setTimeOptions(slots);
          } else {
            // Day is closed, provide minimal options
            setTimeOptions(['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00']);
          }
        } else {
          // Fallback to default hours if no business hours set
          setTimeOptions([
            "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
            "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
            "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00", "19:30", "20:00"
          ]);
        }
      } catch (error) {
        console.error('Error loading business hours:', error);
        // Fallback to extended default hours
        setTimeOptions([
          "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
          "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
          "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00", "19:30", "20:00"
        ]);
      }
    };
    
    loadBusinessHours();
  }, [currentTenant?.id, date]);
  const [time, setTime] = useState("09:00");
  const [type, setType] = useState("");
  const [duration, setDuration] = useState("60");
  const [recurring, setRecurring] = useState(false);
  const [isOutOfOffice, setIsOutOfOffice] = useState(false);
  
  // Event-specific state
  const [eventType, setEventType] = useState("time-block");
  const [allDay, setAllDay] = useState(false);
  
  // Auto-set all day for certain event types
  useEffect(() => {
    const autoAllDayTypes = ['leave', 'sick', 'maternity', 'remote', 'training'];
    if (autoAllDayTypes.includes(eventType)) {
      setAllDay(true);
    }
  }, [eventType]);
  const [endTime, setEndTime] = useState("10:00");
  const [endDate, setEndDate] = useState("");
  const [description, setDescription] = useState("");
  const [leaveReason, setLeaveReason] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [recurringFrequency, setRecurringFrequency] = useState("daily");
  const [recurringInterval, setRecurringInterval] = useState("1");
  const [recurringEndType, setRecurringEndType] = useState("occurrences");
  const [recurringOccurrences, setRecurringOccurrences] = useState("5");
  const [recurringEndDate, setRecurringEndDate] = useState("");
  const [selectedDays, setSelectedDays] = useState([1, 2, 3, 4, 5]); // Default to weekdays
  const [monthlyRecurrenceType, setMonthlyRecurrenceType] = useState('sameDate');
  const [monthlyRecurrenceWeek, setMonthlyRecurrenceWeek] = useState('first');
  const [monthlyRecurrenceDay, setMonthlyRecurrenceDay] = useState(0);
  const [customizeDates, setCustomizeDates] = useState(false);
  const [customAppointmentDates, setCustomAppointmentDates] = useState([]);
  const [customAppointmentTimes, setCustomAppointmentTimes] = useState([]);
  const [status, setStatus] = useState("confirmed");
  const [cancellationReason, setCancellationReason] = useState("");
  const [cancelledBy, setCancelledBy] = useState("");
  const [cancelledAt, setCancelledAt] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [sendEmailConfirmation, setSendEmailConfirmation] = useState(false);
  const [clientQuery, setClientQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [staffId, setStaffId] = useState("");
  const [roomId, setRoomId] = useState("");

  // Conflict detection state
  const [conflicts, setConflicts] = useState({
    clinicianConflict: false,
    roomConflict: false,
    closureConflict: false,
    conflicts: []
  });
  const [detailedConflictInfo, setDetailedConflictInfo] = useState(null);
  const [showMoreTimeSlots, setShowMoreTimeSlots] = useState(false);

  // New state for enhanced recurring features
  const [previewOccurrences, setPreviewOccurrences] = useState([]);
  const [conflictSummary, setConflictSummary] = useState({ total: 0, hasConflicts: false });
  const [showRecurringConflictDialog, setShowRecurringConflictDialog] = useState(false);
  const [recurringConflictResults, setRecurringConflictResults] = useState(null);

  // Data lists
  const [staffList, setStaffList] = useState([]);
  const [roomList, setRoomList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [treatmentTypes, setTreatmentTypes] = useState([]);
  
  // Computed properties
  const availableStaff = staffList.filter(staff => staff.role !== 'inactive');
  const filteredClients = clientList.filter(client => 
    client.name?.toLowerCase().includes(clientQuery.toLowerCase()) ||
    client.email?.toLowerCase().includes(clientQuery.toLowerCase())
  );

  // Load data on mount and when modal opens
  useEffect(() => {
    const loadData = async () => {
      try {
        const [staff, rooms, clients, settings] = await Promise.all([
          getStaff(),
          getRooms(),
          clientService.getAllClients(),
          settingsService.getSettings()
        ]);

        console.log('Loaded staff:', staff);
        if (staff && staff.length > 0) {
          console.log('Staff structure:', staff.map(s => ({
            id: s.id,
            name: s.name,
            role: s.role,
            fullName: s.fullName,
            firstName: s.firstName,
            lastName: s.lastName,
            email: s.email,
            allFields: Object.keys(s)
          })));
        }
        console.log('Loaded clients:', clients);
        console.log('🔍 AppointmentModal - Loaded settings:', settings);
        console.log('🔍 AppointmentModal - Settings treatmentRates:', settings?.treatmentRates);
        console.log('🔍 AppointmentModal - Settings treatmentTypes:', settings?.treatmentTypes);
        console.log('🔍 AppointmentModal - Settings keys:', settings ? Object.keys(settings) : 'no settings');

        setStaffList(Array.isArray(staff) ? staff : []);
        setRoomList(Array.isArray(rooms) ? rooms : []);
        setClientList(Array.isArray(clients) ? clients : []);
        
        // Load treatment types - check both treatmentRates (AdminSettings) and treatmentTypes (legacy)
        let treatmentTypesData = [];
        
        if (settings && settings.treatmentRates) {
          // Convert treatmentRates object to array format for dropdown (AdminSettings format)
          treatmentTypesData = Object.entries(settings.treatmentRates).map(([name, rateInfo]) => ({
            name: name,
            pricingType: rateInfo.type, // 'hourly' or 'flat'
            value: rateInfo.value
          }));
        } else if (settings && settings.treatmentTypes) {
          // Use existing treatmentTypes array (legacy format)
          treatmentTypesData = settings.treatmentTypes;
        } else {
          // Fallback to default types if no treatment data found
          treatmentTypesData = [
            { name: 'Physiotherapy', pricingType: 'hourly', value: 150 },
            { name: 'Massage Therapy', pricingType: 'hourly', value: 120 },
            { name: 'Consultation', pricingType: 'flat', value: 80 }
          ];
        }
        
        console.log('Final treatmentTypesData:', treatmentTypesData);
        setTreatmentTypes(treatmentTypesData);
        
        // Set default type to first available treatment if not already set
        if (!type && treatmentTypesData.length > 0) {
          setType(treatmentTypesData[0].name);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setStaffList([]);
        setRoomList([]);
        setClientList([]);
      }
    };

    loadData();
  }, [slot, existing]); // Reload when modal opens with new data

  // Initialize form with appointment data or waitlist data
  useEffect(() => {
    if (appointment) {
      setDate(appointment.date || "");
      setTime(appointment.time || "09:00");
      setType(appointment.type || "");
      setDuration(appointment.duration?.toString() || "60");
      setStatus(appointment.status || "confirmed");
      setCancellationReason(appointment.cancellationReason || "");
      setCancelledBy(appointment.cancelledBy || "");
      setCancelledAt(appointment.cancelledAt || "");
      setStaffId(appointment.staffId || "");
      setRoomId(appointment.roomId || "");
      
      // Handle existing events
      if (appointment.type === 'ooo' || appointment.eventType) {
        setActiveTab('event');
        setEventType(appointment.eventType || 'time-block');
        setAllDay(appointment.allDay || false);
        setEndTime(appointment.endTime || "10:00");
        setEndDate(appointment.endDate || appointment.date || "");
        setDescription(appointment.description || "");
        setLeaveReason(appointment.reason || "");
      }
      
      // Handle recurring data
      if (appointment.isRecurring || appointment.seriesId) {
        setRecurring(true);
        setRecurringFrequency(appointment.recurringFrequency || "weekly");
        setRecurringInterval(appointment.recurringInterval?.toString() || "1");
        setRecurringEndType(appointment.recurringEndType || "occurrences");
        setRecurringOccurrences(appointment.recurringOccurrences?.toString() || "5");
        setRecurringEndDate(appointment.recurringEndDate || "");
      }
      setIsOutOfOffice(appointment.type === 'ooo');

      if (appointment.clientId) {
        const client = clientList.find(c => c.id === appointment.clientId);
        if (client) {
          setSelectedClient(client);
          setClientQuery(client.name);
        }
      }
    } else if (waitlistData) {
      console.log('🎯 AppointmentModal: Processing waitlistData:', waitlistData);
      
      // Pre-populate with booking request data
      if (waitlistData.date) {
        // Parse date from booking format (e.g., "Wednesday 3 September 2025")
        const bookingDate = new Date(waitlistData.date);
        if (!isNaN(bookingDate.getTime())) {
          const year = bookingDate.getFullYear();
          const month = String(bookingDate.getMonth() + 1).padStart(2, '0');
          const day = String(bookingDate.getDate()).padStart(2, '0');
          const formattedDate = `${year}-${month}-${day}`;
          setDate(formattedDate);
          console.log('✅ Set date from booking:', formattedDate);
        }
      }
      
      // Set time from booking (e.g., "13:00 - 14:00")
      if (waitlistData.time) {
        const timeMatch = waitlistData.time.match(/^(\d{2}:\d{2})/);
        if (timeMatch) {
          setTime(timeMatch[1]);
          console.log('✅ Set time from booking:', timeMatch[1]);
        }
      }
      
      // Set service type
      if (waitlistData.service) {
        setType(waitlistData.service);
        console.log('✅ Set service from booking:', waitlistData.service);
      }
      
      // Set duration if available
      if (waitlistData.duration) {
        setDuration(waitlistData.duration.toString());
        console.log('✅ Set duration from booking:', waitlistData.duration);
      }
      
      // Set staff if available
      if (waitlistData.staffId && staffList.length > 0) {
        const staff = staffList.find(s => s.id === waitlistData.staffId);
        if (staff) {
          setStaffId(staff.id);
          console.log('✅ Set staff from booking:', staff.name || staff.fullName);
        }
      }
      
      // Set client information
      if (waitlistData.client) {
        // Try to find existing client first
        let existingClient = null;
        if (clientList.length > 0) {
          existingClient = clientList.find(c => 
            c.email?.toLowerCase() === waitlistData.client.email?.toLowerCase() ||
            c.name?.toLowerCase() === waitlistData.client.name?.toLowerCase()
          );
        }
        
        if (existingClient) {
          setSelectedClient(existingClient);
          setClientQuery(existingClient.name);
          console.log('✅ Found existing client:', existingClient.name);
        } else {
          // Set client query for new client creation
          setClientQuery(waitlistData.client.name);
          console.log('✅ Set new client name:', waitlistData.client.name);
        }
      }
      
      // Set notes if available
      if (waitlistData.notes) {
        setDescription(`Online booking: ${waitlistData.notes}`);
        console.log('✅ Set notes from booking');
      }
    }
  }, [appointment, clientList, waitlistData, staffList]);

  // Initialize form with slot data (for new appointments)
  useEffect(() => {
    if (slot && !appointment) {
      // Format date as YYYY-MM-DD for date input (timezone-safe)
      const slotDate = new Date(slot.date);
      const year = slotDate.getFullYear();
      const month = String(slotDate.getMonth() + 1).padStart(2, '0');
      const day = String(slotDate.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      setDate(formattedDate);
      setTime(slot.hour || "09:00");
      // Reset other fields for new appointment
      setType("Physiotherapy");
      setDuration("60");
      setRecurring(false);
      setStaffId("");
      setRoomId("");
      setStatus("upcoming");
      setSelectedClient(null);
      setClientQuery("");
    }
  }, [slot, appointment]);

  // Effect to update custom dates when recurring settings change
  useEffect(() => {
    if (recurring && date) {
      const baseAppointment = {
        date,
        recurringFrequency,
        recurringInterval,
        recurringEndType,
        recurringOccurrences,
        recurringEndDate,
        selectedDays,
        monthlyRecurrenceType,
        monthlyRecurrenceWeek,
        monthlyRecurrenceDay,
      };
      const appointments = generateRecurringAppointments(baseAppointment);
      const dates = appointments.map(appt => appt.date);
      setCustomAppointmentDates(dates);
    } else {
      setCustomAppointmentDates([]);
    }
  }, [recurring, date, recurringFrequency, recurringInterval, recurringEndType, recurringOccurrences, recurringEndDate, selectedDays, monthlyRecurrenceType, monthlyRecurrenceWeek, monthlyRecurrenceDay]);

  // Duplicate useEffect removed - data loading is handled above

  // Generate preview occurrences when recurring settings change
  useEffect(() => {
    if (!recurring || !date || !time) {
      setPreviewOccurrences([]);
      setConflicts([]);
      setConflictSummary({ total: 0, hasConflicts: false });
      return;
    }

    try {
      // Use the same generateRecurringAppointments function for preview
      const baseAppointment = {
        date,
        time,
        duration: parseInt(duration),
        roomId,
        staffId,
        clientId: selectedClient?.id,
        type,
        status,
        recurringFrequency,
        recurringInterval,
        recurringEndType,
        recurringOccurrences,
        recurringEndDate,
        selectedDays,
        monthlyRecurrenceType,
        monthlyRecurrenceWeek,
        monthlyRecurrenceDay,
      };
      
      // Generate appointments and limit to first 5 for preview
      const allAppointments = generateRecurringAppointments(baseAppointment);
      const previewAppts = allAppointments.slice(0, 5);

      setPreviewOccurrences(previewAppts);

      // Check for conflicts
      const detectedConflicts = checkConflicts(previewAppts, appointments || []);
      setConflicts(detectedConflicts);
      setConflictSummary(getConflictSummary(detectedConflicts));

    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Error generating recurring appointment preview');
      setPreviewOccurrences([]);
      setConflicts([]);
      setConflictSummary({ total: 0, hasConflicts: false });
    }
  }, [recurring, date, time, duration, recurringFrequency, recurringInterval, recurringEndType,
    recurringOccurrences, recurringEndDate, selectedDays, monthlyRecurrenceType, monthlyRecurrenceWeek, monthlyRecurrenceDay,
    roomId, staffId, selectedClient, type, status, appointments]);

  // Single appointment conflict detection
  useEffect(() => {
    // Skip if recurring (handled by above useEffect) or missing required data
    if (recurring || !date || !time || !staffId) {
      // Clear detailed conflict info when not applicable
      if (!recurring) {
        setDetailedConflictInfo(null);
      }
      return;
    }

    try {
      // Create single appointment object for conflict checking
      const singleAppointment = {
        date,
        time,
        duration: parseInt(duration),
        roomId,
        staffId,
        clientId: selectedClient?.id,
        type,
        status,
        id: appointment?.id // Exclude self from conflict detection
      };

      console.log('🔍 Single appointment conflict check:', {
        singleAppointment,
        existingAppointments: appointments?.length || 0,
        staffList: staffList.length,
        roomList: roomList.length,
        timeOptions: timeOptions.length
      });

      // Check for conflicts against existing appointments
      const conflictResult = checkConflicts(singleAppointment, appointments || [], appointment?.id);
      
      console.log('🔍 Conflict result:', conflictResult);
      
      // Convert single appointment conflict result to array format for consistency
      const conflictsArray = conflictResult.hasConflicts ? [{
        occurrenceIndex: 0,
        occurrence: singleAppointment,
        conflicts: conflictResult.conflicts
      }] : [];
      
      setConflicts(conflictsArray);
      setConflictSummary(getConflictSummary(conflictsArray));
      
      // Generate detailed conflict information with solutions
      if (conflictsArray.length > 0) {
        console.log('🔍 Generating detailed conflict info...');
        const detailedInfo = getDetailedConflictInfo(
          conflictsArray,
          singleAppointment,
          appointments || [],
          staffList,
          roomList,
          timeOptions
        );
        console.log('🔍 Detailed conflict info:', detailedInfo);
        setDetailedConflictInfo(detailedInfo);
      } else {
        console.log('🔍 No conflicts, clearing detailed info');
        setDetailedConflictInfo(null);
      }

    } catch (error) {
      console.error('Error checking single appointment conflicts:', error);
      setConflicts([]);
      setConflictSummary({ total: 0, hasConflicts: false });
    }
  }, [recurring, date, time, duration, roomId, staffId, selectedClient, type, status, appointments, appointment?.id, staffList, roomList, timeOptions]);

  // Update monthly recurrence defaults when date changes
  useEffect(() => {
    if (date && recurringFrequency === 'monthly') {
      const startDate = new Date(date);
      const defaults = getDefaultMonthlyMode(startDate);

      if (monthlyRecurrenceType === 'sameWeekday') {
        setMonthlyRecurrenceDay(defaults.sameWeekday.day);
        setMonthlyRecurrenceWeek(defaults.sameWeekday.week === 1 ? 'first' :
          defaults.sameWeekday.week === 2 ? 'second' :
            defaults.sameWeekday.week === 3 ? 'third' :
              defaults.sameWeekday.week === 4 ? 'fourth' : 'last');
      } else if (monthlyRecurrenceType === 'lastWeekday') {
        setMonthlyRecurrenceDay(defaults.lastWeekday.day);
      }
    }
  }, [date, recurringFrequency, monthlyRecurrenceType]);

  // Removed duplicate handleSave function - using the newer version below that handles both appointments and events

  const getNthWeekdayOfMonth = (year, month, dayOfWeek, weekNumber) => {
    const date = new Date(Date.UTC(year, month, 1));
    if (weekNumber === 5) { // 'last'
      date.setUTCMonth(date.getUTCMonth() + 1);
      date.setUTCDate(0);
      const lastDay = date.getUTCDay();
      const diff = lastDay - dayOfWeek;
      date.setUTCDate(date.getUTCDate() - (diff < 0 ? diff + 7 : diff));
    } else {
      const firstDay = date.getUTCDay();
      const diff = dayOfWeek - firstDay;
      date.setUTCDate(1 + (diff < 0 ? diff + 7 : diff) + (weekNumber - 1) * 7);
    }
    return date;
  };

  // Generate recurring appointments based on settings
  const generateRecurringAppointments = (baseAppointment) => {
    const appointments = [];
    const seriesId = `series_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const {
      date,
      recurringFrequency,
      recurringInterval,
      recurringEndType,
      recurringOccurrences,
      recurringEndDate,
      selectedDays,
    } = baseAppointment;

    // Use UTC to avoid timezone-related date calculation errors
    const [startYear, startMonth, startDay] = date.split('-').map(Number);
    const startDate = new Date(Date.UTC(startYear, startMonth - 1, startDay));
    const originalDayOfMonth = startDate.getUTCDate();

    let currentDate = new Date(startDate);
    let occurrenceCount = 0;
    const maxOccurrences = recurringEndType === "occurrences" ? parseInt(recurringOccurrences, 10) || 1 : 1000; // Back to 1000 for end date mode
    const endDate = recurringEndType === "date" && recurringEndDate ? new Date(recurringEndDate + 'T23:59:59') : null;
    const interval = parseInt(recurringInterval, 10) || 1;
    

    let loopGuard = 0; // Safety break to prevent infinite loops

    if (recurringFrequency === 'daily') {
      // Daily recurrence logic
      while (occurrenceCount < maxOccurrences && loopGuard < 1000) {
        loopGuard++;
        
        // Check end date first
        if (endDate && currentDate > endDate) break;

        appointments.push({
          ...baseAppointment,
          date: currentDate.toISOString().split('T')[0],
          seriesId,
          seriesIndex: occurrenceCount,
          isRecurring: true,
        });
        occurrenceCount++;

        // Move to next day
        currentDate.setUTCDate(currentDate.getUTCDate() + interval);
      }
    } else if (recurringFrequency === 'weekly') {
      // Weekly recurrence logic
      const selectedDays = baseAppointment.selectedDays.sort((a, b) => a - b);
      // Start from the original start date
      currentDate = new Date(startDate);
      
      while (occurrenceCount < maxOccurrences && loopGuard < 1000) {
        loopGuard++;
        
        // Check end date first - break before processing
        if (endDate && currentDate > endDate) break;
        
        // Check if current date is one of the selected days
        if (selectedDays.includes(currentDate.getUTCDay())) {
          appointments.push({
            ...baseAppointment,
            date: currentDate.toISOString().split('T')[0],
            seriesId,
            seriesIndex: occurrenceCount,
            isRecurring: true,
          });
          occurrenceCount++;
        }

        // Move to next day
        currentDate.setUTCDate(currentDate.getUTCDate() + 1);
        
        // Check if we've reached max occurrences or end date after advancing
        if (occurrenceCount >= maxOccurrences) break;
        if (endDate && currentDate > endDate) break;
      }
    } else {
      // Monthly recurrence logic
      let currentMonthDate = new Date(baseAppointment.date + 'T00:00:00');
      const originalStartDate = new Date(baseAppointment.date + 'T00:00:00');
      const originalDayOfWeek = originalStartDate.getUTCDay();
      const originalDayOfMonth = originalStartDate.getUTCDate();

      while (occurrenceCount < maxOccurrences && loopGuard < 500) {
        loopGuard++;
        let appointmentDate;

        if (monthlyRecurrenceType === 'sameWeekday') {
          // Same weekday each month (e.g., fifth Saturday)
          const weekMap = { first: 1, second: 2, third: 3, fourth: 4, last: 5 };
          const weekNumber = weekMap[monthlyRecurrenceWeek] || Math.ceil(originalStartDate.getUTCDate() / 7);
          appointmentDate = getNthWeekdayOfMonth(currentMonthDate.getUTCFullYear(), currentMonthDate.getUTCMonth(), originalDayOfWeek, weekNumber);
        } else if (monthlyRecurrenceType === 'lastWeekday') {
          // Last weekday of month (e.g., last Saturday)
          appointmentDate = getNthWeekdayOfMonth(currentMonthDate.getUTCFullYear(), currentMonthDate.getUTCMonth(), originalDayOfWeek, 5); // 5 = last
        } else {
          // Same date each month (default)
          const year = currentMonthDate.getUTCFullYear();
          const month = currentMonthDate.getUTCMonth();
          appointmentDate = new Date(Date.UTC(year, month, originalDayOfMonth));
          // Handle months with fewer days (e.g., Feb 30th -> Feb 28th)
          if (appointmentDate.getUTCMonth() !== month) {
            appointmentDate = new Date(Date.UTC(year, month + 1, 0)); // Last day of the month
          }
        }

        // Check end date before adding appointment
        if (endDate && appointmentDate > endDate) break;

        // Only add if the appointment date is valid and not before the original start date
        if (appointmentDate >= originalStartDate && appointmentDate.getUTCFullYear() > 1970) {
          appointments.push({
            ...baseAppointment,
            date: appointmentDate.toISOString().split('T')[0],
            seriesId,
            seriesIndex: occurrenceCount,
            isRecurring: true,
          });
          occurrenceCount++;
        }

        if (occurrenceCount >= maxOccurrences) break;

        // Move to next month with the specified interval
        currentMonthDate.setUTCMonth(currentMonthDate.getUTCMonth() + interval);
      }
    }

    return appointments;
  };

  const handleSave = async () => {
    console.log('🎯 Save button clicked!');
    console.log('Current tab:', activeTab);
    console.log('Event type:', eventType);
    
    if (activeTab === 'event') {
      return handleEventSave();
    }
    
    return handleAppointmentSave();
  };

  const handleAppointmentSave = async () => {
    console.log('💾 Saving appointment...');
    
    // Validate required fields
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }
    
    if (!staffId) {
      toast.error('Please select a clinician');
      return;
    }

    try {
      // Build appointment data from form fields
      const appointmentData = {
        clientId: selectedClient.id,
        clientName: selectedClient.name,
        staffId,
        roomId,
        date,
        time,
        duration: parseInt(duration),
        type,
        status,
        cancellationReason: status === "cancelled" ? cancellationReason : "",
        cancelledBy: status === "cancelled" ? cancelledBy : "",
        cancelledAt: status === "cancelled" ? cancelledAt : "",
        notes: '',
        createdAt: new Date().toISOString()
      };

      let saveResult;
      if (appointment?.id) {
        // Update existing appointment
        console.log(`🎯 Updating appointment with ID: ${appointment.id}`);
        saveResult = await appointmentService.updateAppointment(appointment.id, appointmentData);
      } else {
        // Create new appointment(s)
        if (recurring) {
          console.log('🎯 Creating recurring appointment series');
          
          // Add recurring settings to appointment data
          const baseAppointment = {
            ...appointmentData,
            recurringFrequency,
            recurringInterval,
            recurringEndType,
            recurringOccurrences,
            recurringEndDate,
            selectedDays,
            monthlyRecurrenceType,
            monthlyRecurrenceWeek,
            monthlyRecurrenceDay,
          };
          
          // Generate all recurring appointments
          const appointments = generateRecurringAppointments(baseAppointment);
          console.log(`🎯 Generated ${appointments.length} recurring appointments`);
          
          // Save all appointments
          const savePromises = appointments.map(appt => appointmentService.createAppointment(appt));
          const results = await Promise.all(savePromises);
          
          saveResult = results[0]; // Return first appointment for callback compatibility
          toast.success(`Created ${appointments.length} recurring appointments!`);
        } else {
          console.log('🎯 Creating single appointment');
          saveResult = await appointmentService.createAppointment(appointmentData);
          toast.success("Appointment saved!");
        }
      }
      console.log('✅ Save service returned:', saveResult);

      // If this appointment was created from waitlist data, mark the waitlist entry as converted
      if (waitlistData?.waitlistId && saveResult?.id) {
        try {
          console.log('🎯 Converting waitlist entry to appointment:', waitlistData.waitlistId);
          await convertToAppointment(waitlistData.waitlistId, {
            id: saveResult.id,
            ...appointmentData
          });
          console.log('✅ Waitlist entry marked as converted');
        } catch (error) {
          console.error('❌ Error converting waitlist entry:', error);
          // Don't fail the appointment save if waitlist conversion fails
        }
      }

      // Call callbacks to refresh calendar
      console.log('📞 Calling refresh callbacks...');

      // Close modal first
      onClose();

      // Then trigger data reload with a small delay to ensure modal is closed
      setTimeout(() => {
        if (onSaved) {
          console.log('📞 Calling onSaved callback');
          onSaved();
        }
        
        // If this was a waitlist appointment, trigger waitlist refresh
        if (waitlistData?.waitlistId && onWaitlistRefresh) {
          console.log('📞 Calling waitlist refresh callback');
          onWaitlistRefresh();
        }
      }, 100);

      console.log('🏁 Modal closed, refresh scheduled');
    } catch (error) {
      console.error('❌ Error saving appointment:', error);
      console.error('❌ Error details:', error.message, error.stack);
      toast.error(`Failed to save appointment: ${error.message}`);
    }
  };

  const handleDelete = async () => {
    console.log('🗑️ Delete button clicked!');
    console.log('Appointment to delete:', appointment);
    console.log('Appointment ID type:', typeof appointment?.id);
    console.log('Appointment ID value:', appointment?.id);

    if (!appointment) {
      console.error('No appointment to delete!');
      toast.error('No appointment selected for deletion');
      return;
    }

    try {
      console.log(`🎯 Attempting to delete appointment with ID: ${appointment.id}`);
      console.log('🔍 Full appointment object:', JSON.stringify(appointment, null, 2));

      // Delete using appointmentService
      const deleteResult = await appointmentService.deleteAppointment(appointment.id);
      console.log('✅ Delete service returned:', deleteResult);

      // Verify deletion by trying to fetch the appointment
      try {
        const checkAppointment = await appointmentService.getAppointmentById(appointment.id);
        if (checkAppointment) {
          console.error('❌ CRITICAL: Appointment still exists after delete!', checkAppointment);
          toast.error('Delete failed - appointment still exists!');
          return;
        } else {
          console.log('✅ Verification: Appointment successfully removed from database');
        }
      } catch (verifyError) {
        console.log('✅ Verification: Appointment not found (successfully deleted)');
      }

      toast.success("Appointment deleted!");

      // Call callbacks to refresh calendar
      console.log('📞 Calling refresh callbacks...');

      // Close modal first
      onClose();

      // Then trigger data reload with a small delay to ensure modal is closed
      setTimeout(() => {
        if (onDelete) {
          console.log('📞 Calling onDelete callback');
          onDelete();
        }
        if (onSaved) {
          console.log('📞 Calling onSaved callback');
          onSaved();
        }
      }, 100);

      console.log('🏁 Modal closed, refresh scheduled');
    } catch (error) {
      console.error('❌ Error deleting appointment:', error);
      console.error('❌ Error details:', error.message, error.stack);
      toast.error(`Failed to delete appointment: ${error.message}`);
    }
  };
  
  const handleEventSave = async () => {
    console.log('🚫 Saving event...');
    
    if (!staffId) {
      toast.error('Please select a clinician');
      return;
    }
    
    if (!date) {
      toast.error('Please select a date');
      return;
    }
    
    if (!allDay && (!time || !endTime)) {
      toast.error('Please select start and end times');
      return;
    }
    
    if ((eventType === 'leave' || eventType === 'sick') && !leaveReason.trim()) {
      toast.error('Please provide a reason for the leave request');
      return;
    }
    
    try {
      // Calculate duration for OOO events
      const calculateEventDuration = () => {
        if (eventType === 'leave' || eventType === 'sick') {
          // For leave requests, calculate total minutes across date range
          const startDate = new Date(date);
          const endDate = new Date(endTime || date); // endTime holds end date for leave requests
          
          if (startDate.toDateString() === endDate.toDateString()) {
            // Same day - use time difference or full day
            if (allDay) {
              return 1440; // 24 hours in minutes
            } else {
              const [startHours, startMins] = time.split(':').map(Number);
              const [endHours, endMins] = (endTime || time).split(':').map(Number);
              const startTotalMins = startHours * 60 + startMins;
              const endTotalMins = endHours * 60 + endMins;
              return endTotalMins - startTotalMins;
            }
          } else {
            // Multi-day leave - calculate total minutes across all days
            const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
            return daysDiff * 1440; // Full days in minutes
          }
        } else {
          // Regular events
          if (allDay) {
            return 1440; // 24 hours in minutes
          } else {
            const [startHours, startMins] = time.split(':').map(Number);
            const [endHours, endMins] = endTime.split(':').map(Number);
            const startTotalMins = startHours * 60 + startMins;
            const endTotalMins = endHours * 60 + endMins;
            return endTotalMins - startTotalMins;
          }
        }
      };

      const eventData = {
        type: 'ooo',
        eventType,
        date,
        time: allDay ? '00:00' : time,
        endTime: allDay ? '23:59' : endTime,
        endDate: endDate || date,
        duration: calculateEventDuration(),
        allDay,
        staffId,
        roomId: (eventType === 'leave' || eventType === 'sick') ? '' : roomId,
        description: (eventType === 'leave' || eventType === 'sick') ? leaveReason : description,
        reason: (eventType === 'leave' || eventType === 'sick') ? leaveReason : '',
        status: (eventType === 'leave' || eventType === 'sick') ? 'pending' : 'confirmed',
        requiresApproval: eventType === 'leave' || eventType === 'sick',
        createdAt: new Date().toISOString(),
        createdBy: 'current-user' // TODO: Get from auth context
      };
      
      if (recurring) {
        eventData.isRecurring = true;
        eventData.recurringFrequency = recurringFrequency;
        eventData.recurringInterval = parseInt(recurringInterval);
        eventData.recurringEndType = recurringEndType;
        eventData.recurringOccurrences = parseInt(recurringOccurrences);
        eventData.recurringEndDate = recurringEndDate;
      }
      
      console.log('Event data to save:', eventData);
      
      // Calculate days count for helper functions
      const calculateDaysCount = (startDate, endDate, allDay) => {
        if (!startDate || !endDate) return 1;
        const start = new Date(startDate);
        const end = new Date(endDate);
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return 1;
        const diffTime = Math.abs(end - start);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      };

      const daysCount = calculateDaysCount(eventData.date, endDate || eventData.date, allDay);
      
      // Apply approval matrix logic
      if (eventType === 'leave' || eventType === 'maternity') {
        // Handle leave request document
        console.log('Processing leave request...');
        
        // Find the selected staff member to get the name
        const selectedStaff = staffList.find(s => s.id === eventData.staffId);
        const staffName = selectedStaff ? 
          (selectedStaff.name || selectedStaff.fullName || `${selectedStaff.firstName || ''} ${selectedStaff.lastName || ''}`.trim() || selectedStaff.email || 'Unknown Staff') :
          'Unknown Staff';
        
        // Determine status based on approval matrix
        let requestStatus = 'pending';
        let autoApproved = false;
        
        // Approval Matrix Logic
        if (eventType === 'leave') {
          // Annual Leave: Always requires approval
          requestStatus = 'pending';
        } else if (eventType === 'maternity') {
          // Maternity Leave: Always requires approval
          requestStatus = 'pending';
        }

        const leaveRequest = {
          staffId: eventData.staffId,
          staffName: staffName,
          type: eventType,
          startDate: eventData.date,
          endDate: endDate || eventData.date,
          reason: leaveReason || '',
          status: requestStatus,
          autoApproved: autoApproved,
          daysCount: daysCount,
          requestedBy: eventData.staffId,
          requestedByName: staffName,
          createdAt: appointment?.id ? appointment.createdAt : new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        // Save to leaveRequests collection
        tenantDataService.setTenantContext(currentTenant.id);
        
        let leaveResult;
        if (appointment?.id) {
          // Update existing leave request
          console.log('Updating existing leave request...');
          // Find corresponding leave request
          const leaveRequests = await tenantDataService.getAll('leaveRequests');
          const existingLeaveRequest = leaveRequests.find(req => 
            req.staffId === eventData.staffId && 
            req.startDate === appointment.date &&
            req.type === appointment.eventType
          );
          
          if (existingLeaveRequest) {
            leaveResult = await tenantDataService.update('leaveRequests', existingLeaveRequest.id, leaveRequest);
            console.log('Leave request updated:', leaveResult);
          } else {
            leaveResult = await tenantDataService.create('leaveRequests', leaveRequest);
            console.log('New leave request created:', leaveResult);
          }
        } else {
          // Create new leave request
          leaveResult = await tenantDataService.create('leaveRequests', leaveRequest);
          console.log('Leave request created:', leaveResult);
        }
        
        // Handle OOO appointment
        let appointmentResult;
        if (appointment?.id) {
          // Update existing appointment
          console.log('Updating existing OOO appointment...');
          appointmentResult = await appointmentService.updateAppointment(appointment.id, {
            ...eventData,
            status: 'pending'
          });
          console.log('OOO appointment updated:', appointmentResult);
        } else {
          // Create new appointment
          appointmentResult = await appointmentService.createAppointment({
            ...eventData,
            status: 'pending'
          });
          console.log('Pending OOO appointment created:', appointmentResult);
        }
        
        toast.success(`${eventType === 'leave' ? 'Leave' : 'Sick leave'} request submitted for approval`);
      } else if (eventType === 'sick') {
        // Handle sick leave - auto-approved for single day, requires approval for multiple days
        console.log('Processing sick leave request...');
        
        const selectedStaff = staffList.find(s => s.id === eventData.staffId);
        const staffName = selectedStaff ? 
          (selectedStaff.name || selectedStaff.fullName || `${selectedStaff.firstName || ''} ${selectedStaff.lastName || ''}`.trim() || selectedStaff.email || 'Unknown Staff') :
          'Unknown Staff';
        
        // Approval Matrix Logic for Sick Leave
        let requestStatus = 'approved'; // Default to approved
        let autoApproved = true;
        
        if (daysCount > 1) {
          // Multi-day sick leave requires approval
          requestStatus = 'pending';
          autoApproved = false;
        }

        const leaveRequest = {
          staffId: eventData.staffId,
          staffName: staffName,
          type: eventType,
          startDate: eventData.date,
          endDate: endDate || eventData.date,
          reason: leaveReason || '',
          status: requestStatus,
          autoApproved: autoApproved,
          daysCount: daysCount,
          requestedBy: eventData.staffId,
          requestedByName: staffName,
          createdAt: appointment?.id ? appointment.createdAt : new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        // Save to leaveRequests collection
        tenantDataService.setTenantContext(currentTenant.id);
        
        let leaveResult;
        if (appointment?.id) {
          // Update existing leave request
          const leaveRequests = await tenantDataService.getAll('leaveRequests');
          const existingLeaveRequest = leaveRequests.find(req => 
            req.staffId === eventData.staffId && 
            req.startDate === appointment.date &&
            req.type === appointment.eventType
          );
          
          if (existingLeaveRequest) {
            leaveResult = await tenantDataService.update('leaveRequests', existingLeaveRequest.id, leaveRequest);
          } else {
            leaveResult = await tenantDataService.create('leaveRequests', leaveRequest);
          }
        } else {
          leaveResult = await tenantDataService.create('leaveRequests', leaveRequest);
        }
        
        // Create or update corresponding OOO appointment
        let appointmentResult;
        if (appointment?.id) {
          appointmentResult = await appointmentService.updateAppointment(appointment.id, {
            ...eventData,
            status: requestStatus === 'approved' ? 'confirmed' : 'pending',
            leaveRequestId: leaveResult.id
          });
        } else {
          appointmentResult = await appointmentService.createAppointment({
            ...eventData,
            status: requestStatus === 'approved' ? 'confirmed' : 'pending',
            leaveRequestId: leaveResult.id
          });
        }
        
        if (autoApproved) {
          toast.success('Sick leave approved automatically');
        } else {
          toast.success('Sick leave request submitted for approval');
        }
      } else if (['remote', 'training'].includes(eventType)) {
        // Handle remote work and training - auto-approved
        console.log(`Processing ${eventType} request...`);
        
        const selectedStaff = staffList.find(s => s.id === eventData.staffId);
        const staffName = selectedStaff ? 
          (selectedStaff.name || selectedStaff.fullName || `${selectedStaff.firstName || ''} ${selectedStaff.lastName || ''}`.trim() || selectedStaff.email || 'Unknown Staff') :
          'Unknown Staff';

        const leaveRequest = {
          staffId: eventData.staffId,
          staffName: staffName,
          type: eventType,
          startDate: eventData.date,
          endDate: endDate || eventData.date,
          reason: leaveReason || '',
          status: 'approved',
          autoApproved: true,
          daysCount: daysCount,
          requestedBy: eventData.staffId,
          requestedByName: staffName,
          createdAt: appointment?.id ? appointment.createdAt : new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        // Save to leaveRequests collection
        tenantDataService.setTenantContext(currentTenant.id);
        
        let leaveResult;
        if (appointment?.id) {
          const leaveRequests = await tenantDataService.getAll('leaveRequests');
          const existingLeaveRequest = leaveRequests.find(req => 
            req.staffId === eventData.staffId && 
            req.startDate === appointment.date &&
            req.type === appointment.eventType
          );
          
          if (existingLeaveRequest) {
            leaveResult = await tenantDataService.update('leaveRequests', existingLeaveRequest.id, leaveRequest);
          } else {
            leaveResult = await tenantDataService.create('leaveRequests', leaveRequest);
          }
        } else {
          leaveResult = await tenantDataService.create('leaveRequests', leaveRequest);
        }
        
        // Create or update corresponding appointment
        let appointmentResult;
        if (appointment?.id) {
          appointmentResult = await appointmentService.updateAppointment(appointment.id, {
            ...eventData,
            status: 'confirmed',
            leaveRequestId: leaveResult.id
          });
        } else {
          appointmentResult = await appointmentService.createAppointment({
            ...eventData,
            status: 'confirmed',
            leaveRequestId: leaveResult.id
          });
        }
        
        toast.success(`${eventType === 'remote' ? 'Remote work' : 'Training'} approved automatically`);
      } else {
        // Handle regular appointments and meetings - auto-confirmed
        const result = await appointmentService.createAppointment({
          ...eventData,
          status: 'confirmed'
        });
        console.log('Event created:', result);
        toast.success('Event created successfully!');
      }
      
      // Close modal and refresh
      onClose();
      if (onSaved) {
        onSaved();
      }
      
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(`Failed to save event: ${error.message}`);
    }
  };

  const confirmDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleCancel = () => {
    const reason = prompt("Reason for cancellation:");
    if (reason !== null) {
      setStatus("cancelled");
      toast.success("Appointment cancelled successfully!");
      
      // Log the cancellation
      logActivity(ACTIVITY_TYPES.APPOINTMENT_CANCELLED, {
        appointmentId: appointment?.id,
        reason: reason
      });
    }
  };

  const handleEmailConfirmation = async () => {
    if (!selectedClient?.email) {
      toast.error("No client email available");
      return;
    }

    if (!date || !time || !staffId) {
      toast.error("Missing appointment details");
      return;
    }

    try {
      const functions = getFunctions();
      const sendAppointmentConfirmation = httpsCallable(functions, 'sendAppointmentConfirmation');
      
      // Find the selected clinician
      const selectedClinician = staffList.find(staff => staff.id === staffId);
      
      // Create appointment start datetime
      const appointmentStart = new Date(`${date}T${time}:00`);
      
      const confirmationData = {
        tenantId: currentTenant.id,
        clientEmail: selectedClient.email,
        clientName: selectedClient.name,
        appointmentDetails: {
          start: appointmentStart.toISOString(),
          date: date,
          time: time,
          duration: parseInt(duration) || 60,
          service: type,
          clinician: selectedClinician?.name || 'Staff Member',
          notes: description || ''
        }
      };

      console.log('📧 Sending appointment confirmation:', confirmationData);
      
      await sendAppointmentConfirmation(confirmationData);
      
      toast.success(`Confirmation email sent to ${selectedClient.email}`);
      
      // Log the email confirmation
      logActivity(ACTIVITY_TYPES.EMAIL_SENT, {
        appointmentId: appointment?.id,
        clientEmail: selectedClient.email,
        type: 'appointment_confirmation'
      });
      
    } catch (error) {
      console.error('Error sending appointment confirmation:', error);
      toast.error('Failed to send confirmation email');
    }
  };

  // Helper functions
  const getOrdinalSuffix = (day) => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const getWeekdayDescription = (date) => {
    const dayName = WEEKDAY_FULL_NAMES[date.getDay()];
    const weekOfMonth = Math.ceil(date.getDate() / 7);
    const positions = ['', 'first', 'second', 'third', 'fourth', 'fifth'];
    return `${positions[weekOfMonth]} ${dayName}`;
  };
  
  // Calculate end time based on duration for appointments
  const calculateEndTime = (startTime, durationMins) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + parseInt(durationMins);
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMins = totalMinutes % 60;
    return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
  };
  
  // Auto-calculate end time when time or duration changes
  useEffect(() => {
    if (activeTab === 'event' && !allDay && time && !appointment) {
      const defaultDuration = eventType === 'meeting' ? 60 : eventType === 'training' ? 120 : 60;
      setEndTime(calculateEndTime(time, defaultDuration));
    }
  }, [time, eventType, allDay, activeTab, appointment]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {appointment ? (activeTab === 'appointment' ? "Edit Appointment" : "Edit Event") : (activeTab === 'appointment' ? "Create Appointment" : "Add Event")}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-200 rounded-full"
            >
              ×
            </button>
          </div>
          
          {/* Tab Navigation */}
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('appointment')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'appointment'
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              📅 Appointment
            </button>
            <button
              onClick={() => setActiveTab('event')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'event'
                  ? 'bg-orange-100 text-orange-700 border border-orange-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              🚫 Add Event
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-grow overflow-y-auto p-6">
          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-6">
            
            {/* Appointment Tab Content */}
            {activeTab === 'appointment' && (
              <div className="space-y-6">
            
            {/* Enhanced Conflict Information - Moved to top for visibility */}
            {detailedConflictInfo && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center text-red-800 mb-3">
                  <FiAlertTriangle className="h-5 w-5 mr-2" />
                  <span className="text-sm font-semibold">
                    {detailedConflictInfo.conflicts.length} scheduling conflict{detailedConflictInfo.conflicts.length !== 1 ? 's' : ''} detected
                  </span>
                </div>
                
                {/* Conflict Details */}
                <div className="space-y-2 mb-4">
                  {detailedConflictInfo.conflicts.map((conflict, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <FiAlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-red-800">{conflict.title}</div>
                        <div className="text-xs text-red-600">{conflict.description}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Smart Solutions */}
                {(detailedConflictInfo.solutions.nextAvailableSlot || detailedConflictInfo.solutions.alternativeRooms.length > 0) && (
                  <div className="border-t border-red-200 pt-3">
                    <div className="text-sm font-medium text-red-800 mb-2">💡 Suggested Solutions:</div>
                    
                    {/* Next Available Time */}
                    {detailedConflictInfo.solutions.nextAvailableSlot && (
                      <div className="mb-2">
                        <div className="flex items-center gap-2 mb-2">
                          <button
                            type="button"
                            onClick={() => {
                              setTime(detailedConflictInfo.solutions.nextAvailableSlot.time);
                              if (detailedConflictInfo.solutions.nextAvailableSlot.date !== date) {
                                setDate(detailedConflictInfo.solutions.nextAvailableSlot.date);
                              }
                            }}
                            className="inline-flex items-center px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs font-medium rounded-full transition-colors"
                          >
                            <FiClock className="h-3 w-3 mr-1" />
                            {detailedConflictInfo.solutions.nextAvailableSlot.message}
                          </button>
                          
                          {detailedConflictInfo.solutions.multipleAvailableSlots?.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setShowMoreTimeSlots(!showMoreTimeSlots)}
                              className="inline-flex items-center px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-full transition-colors"
                            >
                              {showMoreTimeSlots ? 'Show less' : `+${detailedConflictInfo.solutions.multipleAvailableSlots.length - 1} more times`}
                            </button>
                          )}
                        </div>
                        
                        {/* Expanded Time Slots */}
                        {showMoreTimeSlots && detailedConflictInfo.solutions.multipleAvailableSlots?.length > 1 && (
                          <div className="mt-2 p-3 bg-blue-25 border border-blue-100 rounded-lg">
                            <div className="text-xs font-medium text-blue-800 mb-2">Choose from available times:</div>
                            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                              {detailedConflictInfo.solutions.multipleAvailableSlots.map((slot, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => {
                                    setTime(slot.time);
                                    if (slot.date !== date) {
                                      setDate(slot.date);
                                    }
                                    setShowMoreTimeSlots(false);
                                  }}
                                  className="flex flex-col items-start px-2 py-1.5 bg-white hover:bg-blue-50 border border-blue-200 rounded text-xs transition-colors"
                                >
                                  <span className="font-medium text-blue-900">{slot.displayTime}</span>
                                  <span className="text-blue-600 text-xs">{slot.displayDate}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Alternative Rooms */}
                    {detailedConflictInfo.solutions.alternativeRooms.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-xs text-red-700 mb-1">Alternative rooms at same time:</div>
                        <div className="flex flex-wrap gap-1">
                          {detailedConflictInfo.solutions.alternativeRooms.map((room, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => setRoomId(room.id)}
                              className="inline-flex items-center px-2 py-1 bg-green-100 hover:bg-green-200 text-green-800 text-xs font-medium rounded-full transition-colors"
                            >
                              <FiMapPin className="h-3 w-3 mr-1" />
                              {room.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="text-xs text-red-600 mt-3 pt-2 border-t border-red-200">
                  ⚠️ You can still save with conflicts if needed, but it may cause scheduling issues.
                </div>
              </div>
            )}
            
            {/* Date & Time Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                <select
                  value={time}
                  onChange={e => setTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors focus:outline-none"
                  required
                >
                  {timeOptions.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                {/* Timezone indicator */}
                <div className="text-xs text-gray-500 mt-1">
                  Your timezone: <strong>{getUserTimezone()}</strong>
                </div>
              </div>
            </div>

            {/* Type & Duration Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <select
                  value={type}
                  onChange={e => setType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors focus:outline-none"
                  required
                >
                  {treatmentTypes.map(treatment => (
                    <option key={treatment.name} value={treatment.name}>
                      {treatment.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Duration (mins)</label>
                <input
                  type="number"
                  value={duration}
                  onChange={e => setDuration(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  min="15"
                  step="15"
                  required
                />
              </div>
            </div>

            {/* Out of Office Toggle */}
            <div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="outOfOffice"
                  checked={isOutOfOffice}
                  onChange={e => setIsOutOfOffice(e.target.checked)}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                />
                <label htmlFor="outOfOffice" className="ml-2 text-sm font-medium text-gray-700">
                  Mark as Out of Office / Leave
                </label>
              </div>
              {isOutOfOffice && (
                <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-800">
                    <FiAlertTriangle className="inline w-4 h-4 mr-1" />
                    This will block the selected time for the clinician. Room selection is disabled for OOO periods.
                  </p>
                </div>
              )}
            </div>

            {/* Recurring Appointment Section */}
            <div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="recurring"
                  checked={recurring}
                  onChange={e => setRecurring(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <label htmlFor="recurring" className="ml-2 text-sm font-medium text-gray-700">
                  Enable recurring appointment
                </label>
              </div>
              {recurring && (
                <div className="mt-4 pl-6 border-l-2 border-gray-200 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                      <select
                        value={recurringFrequency}
                        onChange={e => setRecurringFrequency(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors focus:outline-none"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Repeat every {recurringInterval} {recurringFrequency === 'daily' ? 'day' + (recurringInterval > 1 ? 's' : '') : 
                                      recurringFrequency === 'weekly' ? 'week' + (recurringInterval > 1 ? 's' : '') : 
                                      'month' + (recurringInterval > 1 ? 's' : '')}
                      </label>
                      <input
                        type="number"
                        value={recurringInterval}
                        onChange={e => setRecurringInterval(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        min="1"
                      />
                    </div>
                  </div>

                  {recurringFrequency === "weekly" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">On Days</label>
                      <div className="flex flex-wrap gap-2">
                        {WEEKDAY_NAMES.map((day, index) => (
                          <button
                            key={day}
                            type="button"
                            onClick={() => {
                              const newSelectedDays = selectedDays.includes(index)
                                ? selectedDays.filter(d => d !== index)
                                : [...selectedDays, index].sort((a, b) => a - b);
                              setSelectedDays(newSelectedDays);
                            }}
                            className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${selectedDays.includes(index)
                              ? 'bg-blue-100 text-blue-800 border-2 border-blue-300'
                              : 'bg-gray-100 text-gray-600 border-2 border-gray-200 hover:bg-gray-200'
                              }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                      {selectedDays.length === 0 && (
                        <p className="text-sm text-red-600 mt-1">Please select at least one day</p>
                      )}
                    </div>
                  )}

                  {recurringFrequency === "monthly" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">Monthly Pattern</label>
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="sameDate"
                            name="monthlyMode"
                            value="sameDate"
                            checked={monthlyRecurrenceType === 'sameDate'}
                            onChange={(e) => setMonthlyRecurrenceType(e.target.value)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <label htmlFor="sameDate" className="ml-2 text-sm text-gray-700">
                            Same date each month {date && `(e.g., ${new Date(date).getDate()}${getOrdinalSuffix(new Date(date).getDate())} of each month)`}
                          </label>
                        </div>

                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="sameWeekday"
                            name="monthlyMode"
                            value="sameWeekday"
                            checked={monthlyRecurrenceType === 'sameWeekday'}
                            onChange={(e) => setMonthlyRecurrenceType(e.target.value)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <label htmlFor="sameWeekday" className="ml-2 text-sm text-gray-700">
                            Same weekday each month {date && `(e.g., ${getWeekdayDescription(new Date(date))})`}
                          </label>
                        </div>

                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="lastWeekday"
                            name="monthlyMode"
                            value="lastWeekday"
                            checked={monthlyRecurrenceType === 'lastWeekday'}
                            onChange={(e) => setMonthlyRecurrenceType(e.target.value)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <label htmlFor="lastWeekday" className="ml-2 text-sm text-gray-700">
                            Last weekday of month {date && `(e.g., last ${WEEKDAY_FULL_NAMES[new Date(date).getDay()]})`}
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ends</label>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="occurrences"
                          name="endType"
                          value="occurrences"
                          checked={recurringEndType === "occurrences"}
                          onChange={(e) => setRecurringEndType(e.target.value)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <label htmlFor="occurrences" className="ml-2 text-sm text-gray-700">
                          After
                        </label>
                        <input
                          type="number"
                          value={recurringOccurrences}
                          onChange={(e) => setRecurringOccurrences(e.target.value)}
                          className="ml-2 w-16 px-2 py-1 border border-gray-300 rounded-md text-sm"
                          min="1"
                          max="100"
                        />
                        <span className="ml-1 text-sm text-gray-700">occurrences</span>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="endDate"
                          name="endType"
                          value="date"
                          checked={recurringEndType === "date"}
                          onChange={(e) => setRecurringEndType(e.target.value)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <label htmlFor="endDate" className="ml-2 text-sm text-gray-700">
                          On
                        </label>
                        <input
                          type="date"
                          value={recurringEndDate}
                          onChange={(e) => setRecurringEndDate(e.target.value)}
                          className="ml-2 px-2 py-1 border border-gray-300 rounded-md text-sm"
                          min={date}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Includes the start date.</p>
                  </div>

                  {/* Customize Dates Checkbox */}
                  <div className="flex items-center mt-4">
                    <input
                      type="checkbox"
                      id="customize-dates"
                      checked={customizeDates}
                      onChange={(e) => setCustomizeDates(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="customize-dates" className="ml-2 text-sm font-medium text-gray-700">
                      Customize dates and times
                    </label>
                  </div>

                  {/* Custom Date and Time Pickers */}
                  {customizeDates && (
                    <div className="mt-4 space-y-3">
                      <label className="block text-sm font-medium text-gray-700">Custom Appointment Dates & Times</label>
                      {customAppointmentDates.map((customDate, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <span className="text-sm text-gray-600 w-20 flex-shrink-0">Appointment {index + 1}:</span>
                          <input
                            type="date"
                            value={customDate}
                            onChange={(e) => {
                              const newDates = [...customAppointmentDates];
                              newDates[index] = e.target.value;
                              setCustomAppointmentDates(newDates);
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors focus:outline-none"
                          />
                          <select
                            value={customAppointmentTimes[index] || time}
                            onChange={(e) => {
                              const newTimes = [...customAppointmentTimes];
                              newTimes[index] = e.target.value;
                              setCustomAppointmentTimes(newTimes);
                            }}
                            className="w-24 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors focus:outline-none text-sm"
                          >
                            {timeOptions.map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => {
                              const newDates = customAppointmentDates.filter((_, i) => i !== index);
                              const newTimes = customAppointmentTimes.filter((_, i) => i !== index);
                              setCustomAppointmentDates(newDates);
                              setCustomAppointmentTimes(newTimes);
                            }}
                            className="text-red-600 hover:text-red-700 p-1"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          setCustomAppointmentDates([...customAppointmentDates, date]);
                          setCustomAppointmentTimes([...customAppointmentTimes, time]);
                        }}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        + Add another appointment
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Client Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
              <div className="relative">
                <input
                  type="text"
                  value={clientQuery}
                  onChange={e => {
                    setClientQuery(e.target.value);
                    setSelectedClient(null);
                  }}
                  placeholder="Start typing name or email..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  required
                />
                {!selectedClient && clientQuery && filteredClients.length > 0 && (
                  <ul className="absolute z-30 bg-white border border-gray-300 rounded-lg mt-1 w-full max-h-32 overflow-auto shadow-lg">
                    {filteredClients.slice(0, 10).map(client => (
                      <li
                        key={client.id}
                        onClick={() => {
                          setSelectedClient(client);
                          setClientQuery(client.name);
                        }}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                      >
                        <div className="font-medium">{client.name}</div>
                        {client.email && <div className="text-gray-500">{client.email}</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            
            {/* Live Preview for Appointments */}
            {activeTab === 'appointment' && previewOccurrences.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preview (next {Math.min(5, previewOccurrences.length)} occurrences)
                </label>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                  {previewOccurrences.map((occurrence, index) => {
                    const hasConflict = conflicts.some && conflicts.some(c => c.occurrenceIndex === index);
                    const conflictDetails = conflicts.find && conflicts.find(c => c.occurrenceIndex === index);

                    return (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-2 rounded text-sm ${
                          hasConflict ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-200'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <FiCalendar className="h-4 w-4 text-gray-400" />
                          <span>{new Date(occurrence.date).toLocaleDateString('en-GB')}</span>
                          <FiClock className="h-4 w-4 text-gray-400" />
                          <span>{occurrence.time}</span>
                          {roomId && (
                            <>
                              <FiMapPin className="h-4 w-4 text-gray-400" />
                              <span>{roomList.find(r => r.id === roomId)?.name || roomId}</span>
                            </>
                          )}
                        </div>
                        {hasConflict && (
                          <div className="flex items-center text-red-600">
                            <FiAlertTriangle className="h-4 w-4 mr-1" />
                            <span className="text-xs">
                              {conflictDetails?.conflicts?.length || 1} conflict{(conflictDetails?.conflicts?.length || 1) !== 1 ? 's' : ''}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

              </div>
            )}

            {/* Staff & Room Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Clinician</label>
                <select
                  value={staffId}
                  onChange={e => setStaffId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors focus:outline-none"
                  required
                >
                  <option value="">
                    Select clinician... ({availableStaff.length} available)
                  </option>
                  {availableStaff.map(s => {
                    // Handle different possible name fields
                    const displayName = s.name || s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.email || 'Unnamed Staff';
                    return (
                      <option key={s.id} value={s.id}>{displayName} ({s.role})</option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Room</label>
                <select
                  value={roomId}
                  onChange={e => setRoomId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors focus:outline-none"
                  required
                >
                  <option value="">Select room...</option>
                  {roomList
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                </select>
              </div>
            </div>

            
            </div>
            )}
            
            {/* Event Tab Content */}
            {activeTab === 'event' && (
              <div className="space-y-6">
                
                {/* Event Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Event Type</label>
                  <select
                    value={eventType}
                    onChange={e => setEventType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors focus:outline-none"
                    required
                  >
                    <option value="time-block">🚫 Time Block (OOO)</option>
                    <option value="meeting">🤝 Meeting</option>
                    <option value="training">📚 Training</option>
                    <option value="remote">🏠 Remote Work</option>
                    <option value="leave">🏖️ Annual Leave</option>
                    <option value="sick">🤒 Sick Leave</option>
                    <option value="maternity">👶 Maternity/Paternity</option>
                    <option value="other">📝 Other</option>
                  </select>
                </div>
                
                {/* Event Type Notices */}
                {(eventType === 'leave' || eventType === 'maternity') && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <FiAlertTriangle className="inline w-4 h-4 mr-1" />
                      This will create a leave request that requires admin approval before becoming active.
                    </p>
                  </div>
                )}
                {eventType === 'sick' && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <FiAlertCircle className="inline w-4 h-4 mr-1" />
                      Sick leave will be automatically approved and an admin notification will be sent.
                    </p>
                  </div>
                )}
                {(eventType === 'training' || eventType === 'remote') && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">
                      <FiCheck className="inline w-4 h-4 mr-1" />
                      This will be automatically confirmed and tracked in your {eventType} totals.
                    </p>
                  </div>
                )}
                
                {/* Date & Time Section */}
                <div className="space-y-4">
                  {/* Date Range Picker for all event types */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                      <input
                        type="date"
                        value={date}
                        onChange={e => setDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors focus:outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                      <input
                        type="date"
                        value={endDate || date} // Default to start date if not set
                        onChange={e => setEndDate(e.target.value)}
                        min={date} // End date can't be before start date
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors focus:outline-none"
                        required
                      />
                    </div>
                  </div>

                  {/* All Day Toggle */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="allDay"
                        checked={allDay}
                        onChange={e => setAllDay(e.target.checked)}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 mr-2"
                      />
                      <label htmlFor="allDay" className="text-sm font-medium text-gray-700">
                        All Day Event
                      </label>
                    </div>
                    
                    {/* Dynamic helper text with day count */}
                    <div className="text-sm text-gray-500">
                      {(() => {
                        if (!date) return '';
                        const startDate = new Date(date);
                        const endDate = new Date(endTime || date);
                        
                        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return '';
                        
                        const diffTime = Math.abs(endDate - startDate);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                        
                        if (diffDays === 1) {
                          return '1 day';
                        } else {
                          return `${diffDays} days (inclusive)`;
                        }
                      })()}
                    </div>
                  </div>
                </div>
                
                {/* Time Selection (if not all day and not auto-all-day types) */}
                {!allDay && !['leave', 'sick', 'maternity', 'remote', 'training'].includes(eventType) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                      <select
                        value={time}
                        onChange={e => setTime(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors focus:outline-none"
                        required
                      >
                        {timeOptions.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      <div className="text-xs text-gray-500 mt-1">
                        Your timezone: <strong>{getUserTimezone()}</strong>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                      <select
                        value={endTime}
                        onChange={e => setEndTime(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors focus:outline-none"
                        required
                      >
                        {timeOptions.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                
                {/* Staff Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Clinician</label>
                  <select
                    value={staffId}
                    onChange={e => setStaffId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors focus:outline-none"
                    required
                  >
                    <option value="">
                      Select clinician... ({availableStaff.length} available)
                    </option>
                    {availableStaff.map(s => {
                      const displayName = s.name || s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.email || 'Unnamed Staff';
                      return (
                        <option key={s.id} value={s.id}>{displayName} ({s.role})</option>
                      );
                    })}
                  </select>
                </div>
                
                {/* Room Selection (disabled for leave/sick) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Room (Optional)</label>
                  <select
                    value={roomId}
                    onChange={e => setRoomId(e.target.value)}
                    disabled={eventType === 'leave' || eventType === 'sick'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors focus:outline-none ${
                      (eventType === 'leave' || eventType === 'sick') ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="">No room required</option>
                    {roomList
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                  </select>
                  {(eventType === 'leave' || eventType === 'sick') && (
                    <p className="text-xs text-gray-500 mt-1">Room selection disabled for leave requests</p>
                  )}
                </div>
                
                {/* Description/Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {(eventType === 'leave' || eventType === 'sick') ? 'Reason for Leave' : 'Description'}
                  </label>
                  <textarea
                    value={(eventType === 'leave' || eventType === 'sick') ? leaveReason : description}
                    onChange={e => (eventType === 'leave' || eventType === 'sick') ? setLeaveReason(e.target.value) : setDescription(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors focus:outline-none"
                    placeholder={(eventType === 'leave' || eventType === 'sick') ? 'Please provide reason for leave request...' : 'Optional description...'}
                    required={eventType === 'leave' || eventType === 'sick'}
                  />
                </div>
                
                {/* Recurring Events */}
                <div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="recurringEvent"
                      checked={recurring}
                      onChange={e => setRecurring(e.target.checked)}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                    />
                    <label htmlFor="recurringEvent" className="ml-2 text-sm font-medium text-gray-700">
                      Repeat this event
                    </label>
                  </div>
                  
                  {recurring && (
                    <div className="mt-4 pl-6 border-l-2 border-orange-200 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                          <select
                            value={recurringFrequency}
                            onChange={e => setRecurringFrequency(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors focus:outline-none"
                          >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Repeat every {recurringInterval} {recurringFrequency === 'daily' ? 'day' + (recurringInterval > 1 ? 's' : '') : 
                                          recurringFrequency === 'weekly' ? 'week' + (recurringInterval > 1 ? 's' : '') : 
                                          'month' + (recurringInterval > 1 ? 's' : '')}
                          </label>
                          <input
                            type="number"
                            value={recurringInterval}
                            onChange={e => setRecurringInterval(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                            min="1"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Ends</label>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              id="eventOccurrences"
                              name="eventEndType"
                              value="occurrences"
                              checked={recurringEndType === "occurrences"}
                              onChange={(e) => setRecurringEndType(e.target.value)}
                              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                            />
                            <label htmlFor="eventOccurrences" className="ml-2 text-sm text-gray-700">
                              After
                            </label>
                            <input
                              type="number"
                              value={recurringOccurrences}
                              onChange={(e) => setRecurringOccurrences(e.target.value)}
                              className="ml-2 w-16 px-2 py-1 border border-gray-300 rounded-md text-sm"
                              min="1"
                              max="100"
                            />
                            <span className="ml-1 text-sm text-gray-700">occurrences</span>
                          </div>
                          
                          <div className="flex items-center">
                            <input
                              type="radio"
                              id="eventEndDate"
                              name="eventEndType"
                              value="date"
                              checked={recurringEndType === "date"}
                              onChange={(e) => setRecurringEndType(e.target.value)}
                              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                            />
                            <label htmlFor="eventEndDate" className="ml-2 text-sm text-gray-700">
                              On
                            </label>
                            <input
                              type="date"
                              value={recurringEndDate}
                              onChange={(e) => setRecurringEndDate(e.target.value)}
                              className="ml-2 px-2 py-1 border border-gray-300 rounded-md text-sm"
                              min={date}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
              </div>
            )}
            
          </form>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex-shrink-0">
          {/* Status Section - Only for appointments */}
          {activeTab === 'appointment' && (
            <div className="mb-4">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-medium text-gray-700">Status:</span>
                {appointment && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        console.log('🔍 Create Invoice clicked - appointment:', appointment);
                        console.log('🔍 Create Invoice clicked - appointment.type:', appointment?.type);
                        setShowInvoiceModal(true);
                      }}
                      className="px-3 py-1.5 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center gap-1.5 text-sm"
                    >
                      <FiFileText className="w-3.5 h-3.5" />
                      Create Invoice
                    </button>
                    <button
                      type="button"
                      onClick={handleEmailConfirmation}
                      className="px-3 py-1.5 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors flex items-center gap-1.5 text-sm"
                    >
                      <FiMail className="w-3.5 h-3.5" />
                      Email Confirmation
                    </button>
                    <button
                      type="button"
                      onClick={confirmDelete}
                      className="px-3 py-1.5 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors flex items-center gap-1.5 text-sm"
                    >
                      <FiTrash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setStatus("upcoming")}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${status === "upcoming"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                >
                  Upcoming
                </button>
                <button
                  type="button"
                  onClick={() => setStatus("attended")}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${status === "attended"
                    ? "bg-green-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                >
                  Attended
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${status === "cancelled"
                    ? "bg-red-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setStatus("dna")}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${status === "dna"
                    ? "bg-orange-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                >
                  DNA
                </button>
              </div>
            </div>
          )}

          {/* Cancellation Details */}
          {status === "cancelled" && cancellationReason && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="text-sm font-medium text-red-800 mb-2">Cancellation Details</h4>
              <div className="text-sm text-red-700 space-y-1">
                <div><strong>Reason:</strong> {cancellationReason}</div>
                {cancelledBy && <div><strong>Cancelled by:</strong> {cancelledBy}</div>}
                {cancelledAt && <div><strong>Cancelled at:</strong> {new Date(cancelledAt).toLocaleString()}</div>}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between items-center">
            <div className="flex justify-between items-center w-full">
              {/* Delete button - only show for existing events/appointments */}
              {existing && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <FiTrash2 className="w-4 h-4" />
                  Delete {activeTab === 'appointment' ? 'Appointment' : 'Event'}
                </button>
              )}
              
              <div className="flex space-x-3 ml-auto">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center ${
                    activeTab === 'appointment' 
                      ? (conflictSummary.hasConflicts ? 'bg-yellow-600 hover:bg-yellow-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white')
                      : 'bg-orange-600 hover:bg-orange-700 text-white'
                  }`}
                >
                  {activeTab === 'appointment' && conflictSummary.hasConflicts && (
                    <>
                      <FiAlertTriangle className="h-4 w-4 mr-2" />
                      {conflictSummary.total} conflicts •{' '}
                    </>
                  )}
                  {activeTab === 'appointment' ? 'Save Appointment' : 
                   (eventType === 'leave' || eventType === 'sick') ? 'Submit Request' : 'Save Event'}
                  {activeTab === 'appointment' && recurring && previewOccurrences.length > 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete {activeTab === 'appointment' ? 'Appointment' : 'Event'}</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this {activeTab}? This action cannot be undone.
              </p>
              {activeTab === 'appointment' && selectedClient?.email && (
                <div className="flex items-center mt-4">
                  <input
                    type="checkbox"
                    id="sendEmailConfirmation"
                    checked={sendEmailConfirmation}
                    onChange={(e) => setSendEmailConfirmation(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="sendEmailConfirmation" className="ml-2 text-sm text-gray-700">
                    Send email confirmation to {selectedClient.email}
                  </label>
                </div>
              )}
              <div className="flex gap-3 justify-end pt-6 border-t">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    handleDelete();
                  }}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors flex items-center gap-2"
                >
                  <FiTrash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invoice Modal */}
        {showInvoiceModal && (
          <InvoiceModal
            appointment={{
              ...appointment,
              client: selectedClient,
              clientName: selectedClient?.name || appointment?.clientName,
              clientEmail: selectedClient?.email || appointment?.clientEmail
            }}
            onClose={() => {
              console.log('🔍 InvoiceModal closing');
              setShowInvoiceModal(false);
            }}
            onSave={() => {
              console.log('🔍 InvoiceModal saved');
              setShowInvoiceModal(false);
              toast.success('Invoice created successfully!');
            }}
          />
        )}
      </div>
    </div>
  );
}

export default AppointmentModal;
